import React, { useMemo, useCallback } from 'react';
import * as RadixSlider from '@radix-ui/react-slider';
import { clsx } from 'clsx';
import { SliderRuler } from '../SliderRuler';
import styles from './Slider.module.scss';

export interface SliderProps {
  /**
   * Current value of the slider
   */
  value: number;

  /**
   * Callback fired when the value changes
   */
  onChange: (value: number) => void;

  /**
   * Minimum value for range slider
   */
  min?: number;

  /**
   * Maximum value for range slider
   */
  max?: number;

  /**
   * Step value for range slider
   */
  step?: number;

  /**
   * Array of step values for discrete slider
   */
  steps?: number[];

  /**
   * Whether the slider is disabled
   */
  disabled?: boolean;

  /**
   * Custom formatter for value display
   */
  formatValue?: (value: number) => string;

  /**
   * Slider variant - amount shows min/max labels, period shows step marks
   */
  variant?: 'amount' | 'period';

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Additional CSS class name for container
   */
  containerClassName?: string;

  /**
   * Whether to show the ruler below the slider
   */
  showRuler?: boolean;

  /**
   * Major tick values for the ruler (where labels will be shown)
   */
  rulerMajorTicks?: number[];

  /**
   * Custom formatter for ruler label display
   */
  rulerFormatLabel?: (value: number) => string;
}

export const Slider: React.FC<SliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  steps,
  disabled = false,
  formatValue,
  variant = 'amount',
  className,
  containerClassName,
  showRuler = false,
  rulerMajorTicks,
  rulerFormatLabel,
}) => {
  const isStepsSlider = Array.isArray(steps) && steps.length > 0;
  const showStepMarks = variant === 'period' && isStepsSlider;
  const showMinMaxLabels = variant === 'amount';

  const effectiveMin = isStepsSlider ? Math.min(...steps) : min;
  const effectiveMax = isStepsSlider ? Math.max(...steps) : max;

  const getPercentage = useCallback(
    (val: number) =>
      ((val - effectiveMin) / (effectiveMax - effectiveMin)) * 100,
    [effectiveMin, effectiveMax]
  );

  const findClosestStep = useCallback(
    (val: number) => {
      if (!isStepsSlider || !steps) return val;
      return steps.reduce((prev, curr) =>
        Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev
      );
    },
    [isStepsSlider, steps]
  );

  const radixValue = useMemo(() => [value], [value]);

  const handleRadixChange = useCallback(
    (vals: number[]) => {
      if (disabled) return;
      const raw = vals[0] ?? value;
      let finalValue = raw;
      if (isStepsSlider) {
        finalValue = findClosestStep(raw);
      } else {
        const clamped = Math.max(effectiveMin, Math.min(effectiveMax, raw));
        finalValue = Math.round(clamped / step) * step;
      }
      onChange(finalValue);
    },
    [
      disabled,
      isStepsSlider,
      findClosestStep,
      effectiveMin,
      effectiveMax,
      step,
      onChange,
      value,
    ]
  );

  const formatDisplayValue = useCallback(
    (val: number) => (formatValue ? formatValue(val) : val.toString()),
    [formatValue]
  );

  const containerClasses = clsx(styles.container, containerClassName);
  const sliderRootClasses = clsx(
    styles.slider,
    { [styles.disabled]: disabled },
    className
  );
  const trackClasses = clsx(styles.track, { [styles.trackDisabled]: disabled });
  const rangeClasses = clsx(styles.fill);
  const thumbClasses = clsx(styles.thumb, { [styles.thumbDisabled]: disabled });

  // Nothing to choose: a single possible value.
  if (effectiveMin >= effectiveMax) {
    return null;
  }

  return (
    <div className={containerClasses}>
      <div className={styles.sliderContainer}>
        <RadixSlider.Root
          className={sliderRootClasses}
          value={radixValue}
          onValueChange={handleRadixChange}
          min={effectiveMin}
          max={effectiveMax}
          step={isStepsSlider ? 1 : step}
          disabled={disabled}
          aria-label="Slider"
          aria-valuetext={formatDisplayValue(value)}
        >
          <RadixSlider.Track className={trackClasses}>
            <RadixSlider.Range className={rangeClasses} />
          </RadixSlider.Track>
          <RadixSlider.Thumb className={thumbClasses} />
          {showStepMarks && steps && (
            <div className={styles.stepMarks}>
              {steps.map((stepValue) => (
                <div
                  key={stepValue}
                  className={clsx(styles.stepMark, {
                    [styles.stepMarkActive]: stepValue === value,
                  })}
                  style={{ left: `${getPercentage(stepValue)}%` }}
                />
              ))}
            </div>
          )}
        </RadixSlider.Root>

        {showMinMaxLabels && !showRuler && (
          <div className={styles.labels}>
            <span className={styles.label}>
              {formatDisplayValue(effectiveMin)}
            </span>
            <span className={styles.label}>
              {formatDisplayValue(effectiveMax)}
            </span>
          </div>
        )}
      </div>
      {showRuler && (
        <SliderRuler
          min={effectiveMin}
          max={effectiveMax}
          step={step}
          majorTicks={rulerMajorTicks}
          formatLabel={rulerFormatLabel}
        />
      )}
    </div>
  );
};

Slider.displayName = 'Slider';
